import { vEquipmentType } from '../equipment/utils.ts'
import {
  vArray,
  vBoolean,
  vCoerceArray,
  vDate,
  vDateRange,
  vFallback,
  vInt,
  vMaxValue,
  vMinValue,
  vObject,
  vObjectId,
  vOmit,
  vOptional,
  vOptionalDef,
  type vOutput,
  vPick,
  vPicklist,
  vPipe,
  vString,
  vStringToNumber,
} from '../validation/index.ts'
import { auditStatuses } from './audit.ts'
import {
  modes,
  pricingMethods,
  pricingTypes,
  shipmentDirections,
} from './common.ts'
import { loadDocTypes } from './load-document.ts'
import { shipmentStatuses } from './shipment.ts'
import { type SortDir, sortDirSchema } from './sort.ts'

export const shipmentSortTypes = [
  'pickupDate',
  'messageDate',
  'deliveryDate',
  'status',
  'carrier',
  'createdDate',
  'bookedDate',
] as const
export type ShipmentSortType = (typeof shipmentSortTypes)[number]

export const recentShipmentFilters = [
  'recent',
  'saved',
  'booking-error',
  'rejected-tender',
  'exhausted-route-guide',
] as const

const sortBySchema = vPicklist(shipmentSortTypes)
const sortSchema = vObject({
  sortBy: sortBySchema,
  sortDirection: vFallback(sortDirSchema, -1 as SortDir),
})
export type SortBase = vOutput<typeof sortSchema>
const statusSchema = vArray(vPicklist(shipmentStatuses))
export const loadSearchBaseSchema = vObject({
  search: vOptional(vString()),
})
export const loadFilterSchema = vObject({
  ...loadSearchBaseSchema.entries,
  bolNumber: vOptional(vString()),
  bookedBy: vOptional(vArray(vString())),
  bookedDate: vOptional(vDateRange()),
  carrier: vOptional(vArray(vString())),
  createdDate: vOptional(vDateRange()),
  destAccount: vOptional(vArray(vString())),
  destCity: vOptional(vArray(vString())),
  destCountry: vOptional(vArray(vString())),
  destState: vOptional(vArray(vString())),
  destZip: vOptional(vArray(vString())),
  direction: vOptional(vArray(vPicklist(shipmentDirections))),
  equipmentType: vOptional(vCoerceArray(vEquipmentType())),
  isArchived: vOptional(vBoolean()),
  isFreightCollect: vOptional(vBoolean()),
  isInterline: vOptional(vBoolean()),
  isSpot: vOptional(vBoolean()),
  location: vOptional(vArray(vString())),
  mode: vOptional(vCoerceArray(vPicklist(modes))),
  originAccount: vOptional(vArray(vString())),
  originCity: vOptional(vArray(vString())),
  originCountry: vOptional(vArray(vString())),
  originState: vOptional(vArray(vString())),
  originZip: vOptional(vArray(vString())),
  pickupConfirmationNumber: vOptional(vString()),
  pickupDate: vOptional(vDateRange()),
  pricingMethod: vOptional(vArray(vPicklist(pricingMethods))),
  pricingType: vOptional(vArray(vPicklist(pricingTypes))),
  quotedBy: vOptional(vArray(vString())),
  referenceNumber: vOptional(vString()),
  status: vOptional(statusSchema),
  tag: vOptional(vArray(vString())),
  glCode: vOptional(vArray(vString())),
  trackingNumber: vOptional(vString()),
  isVendor: vOptional(vBoolean()),
  vendors: vOptional(vCoerceArray(vObjectId())),
  upload: vOptional(vCoerceArray(vObjectId())),
})
export type LoadFilterBase = vOutput<typeof loadFilterSchema>

const skipLimitSchema = vObject({
  limit: vFallback(vPipe(vStringToNumber(), vInt(), vMaxValue(20)), 20),
  skip: vFallback(vPipe(vStringToNumber(), vInt(), vMinValue(0)), 0),
})

const inProcessStatuses = ['awarded', 'pending'] as const
export const inProcessFilterSchema = vObject({
  ...sortSchema.entries,
  ...skipLimitSchema.entries,
  ...loadFilterSchema.entries,
  isLiveLoad: vOptional(vBoolean()),
  recentType: vOptional(vPicklist(recentShipmentFilters)),
  sortBy: vFallback(sortBySchema, 'pickupDate'),
  status: vOptionalDef(statusSchema, [...inProcessStatuses]),
})
export type InProcessLookupDTO = vOutput<typeof inProcessFilterSchema>

export const readyCompletedStatuses = [
  'confirmed',
  'picked-up',
  'delivered',
  'canceled',
] as const
export const readyCompletedFilterSchema = vObject({
  ...sortSchema.entries,
  ...skipLimitSchema.entries,
  ...loadFilterSchema.entries,
  auditStatus: vOptional(vCoerceArray(vPicklist(auditStatuses))),
  deliveryDate: vOptional(vDateRange()),
  hasInvoice: vOptional(vBoolean()),
  hasAmountChanged: vOptional(vBoolean()),
  sortBy: vFallback(sortBySchema, 'pickupDate'),
  status: vOptionalDef(statusSchema, [...readyCompletedStatuses]),
  missingDocs: vOptional(vCoerceArray(vPicklist(loadDocTypes))),
  hasDocs: vOptional(vCoerceArray(vPicklist(loadDocTypes))),
  missingTag: vOptional(vCoerceArray(vString())),
})
export type ReadyCompletedLookupDTO = vOutput<typeof readyCompletedFilterSchema>

export const dashboardTileFilterSchema = vOmit(readyCompletedFilterSchema, [
  'sortBy',
  'sortDirection',
  'skip',
  'limit',
])
export type DashboardTileFilterDTO = vOutput<typeof dashboardTileFilterSchema>

export const calendarFilterSchema = vObject({
  ...vOmit(readyCompletedFilterSchema, [
    'sortBy',
    'sortDirection',
    'skip',
    'limit',
  ]).entries,
  dateRange: vDateRange(),
})
export type CalendarLookupDTO = vOutput<typeof calendarFilterSchema>

export const externalApiFilterSchema = vObject({
  ...loadFilterSchema.entries,
  continuationToken: vOptional(vString()),
  createdDateStart: vOptional(vDate()),
  createdDateEnd: vOptional(vDate()),
  deliveryDateStart: vOptional(vDate()),
  deliveryDateEnd: vOptional(vDate()),
  bookedDateStart: vOptional(vDate()),
  bookedDateEnd: vOptional(vDate()),
  pickupDateStart: vOptional(vDate()),
  pickupDateEnd: vOptional(vDate()),
  auditStatus: vOptional(vPicklist(auditStatuses)),
  status: vOptionalDef(vCoerceArray(vPicklist(shipmentStatuses)), [
    ...shipmentStatuses,
  ]),
  originAccount: vOptional(vCoerceArray(vString())),
  originCity: vOptional(vCoerceArray(vString())),
  originCountry: vOptional(vCoerceArray(vString())),
  originState: vOptional(vCoerceArray(vString())),
  originZip: vOptional(vCoerceArray(vString())),
  destAccount: vOptional(vCoerceArray(vString())),
  destCity: vOptional(vCoerceArray(vString())),
  destCountry: vOptional(vCoerceArray(vString())),
  destState: vOptional(vCoerceArray(vString())),
  destZip: vOptional(vCoerceArray(vString())),
})
export type ExternalSearchReqDTO = vOutput<typeof externalApiFilterSchema>

export const auditSortTypes = ['pickupDate', 'acceptedDate'] as const
export type AuditSortType = (typeof auditSortTypes)[number]

export const auditListFilterSchema = vObject({
  ...vPick(readyCompletedFilterSchema, [
    'bookedDate',
    'carrier',
    'createdDate',
    'deliveryDate',
    'destAccount',
    'destCity',
    'destCountry',
    'destState',
    'destZip',
    'direction',
    'equipmentType',
    'hasInvoice',
    'isInterline',
    'limit',
    'location',
    'mode',
    'originAccount',
    'originCity',
    'originCountry',
    'originState',
    'originZip',
    'pickupDate',
    'pricingMethod',
    'pricingType',
    'search',
    'tag',
    'missingTag',
    'glCode',
    'isVendor',
    'vendors',
  ]).entries,
  status: vOptional(vCoerceArray(vPicklist(auditStatuses))),
  hasAmountChanged: vOptional(vBoolean()),
  continuationToken: vOptional(vString()),
  sortBy: vOptional(vPicklist(auditSortTypes)),
  sortDirection: vOptional(sortDirSchema),
})
export type AuditListFilterDTO = vOutput<typeof auditListFilterSchema>
